import {
  Component,
  computed,
  inject,
  signal,
  input,
  output,
  OnInit,
  ElementRef,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { MessagesModule } from 'primeng/messages';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { LoadingOverlayComponent } from '../shared/loading-overlay.component';
import {
  BusquedaClienteDialogComponent,
  BuscadorClienteOrigen,
  ClienteSeleccionado
} from '../common/busquedas/busqueda-cliente-dialog.component';
import {
  BusquedaCentroTrabajoDialogComponent,
  BuscadorCentroTrabajoOrigen,
  CentroTrabajoSeleccionado
} from '../common/busquedas/busqueda-centro-trabajo-dialog.component';
import {
  BusquedaTareaDialogComponent,
  TareaSeleccionada
} from '../common/busquedas/busqueda-tarea-dialog.component';
import {
  BusquedaAsesorDialogComponent,
  AsesorSeleccionado
} from '../common/busquedas/busqueda-asesor-dialog.component';
import {
  Persona,
  getNombreCompleto
} from 'src/app/domain/model/persona/persona';
import { SinOcRepository } from 'src/app/domain/service/sin-oc/sin-oc-repository';
import { Municipio } from 'src/app/domain/model/municipio/municipio';
import {
  CrearTareaRequest,
  CrearTareaResponse,
  OrdenServicioDetalleHidratada,
  PrepararIngresoCumplimientoRequest
} from 'src/app/domain/model/sin-oc/sin-oc';
import { Oficina } from 'src/app/domain/model/oficina/oficina';
import {
  BusquedaProductoDialogComponent,
  ProductoSeleccionado
} from '../common/busquedas/busqueda-producto-dialog.component';
import {
  BusquedaMunicipioDialogComponent,
  MunicipioSeleccionado
} from '../common/busquedas/busqueda-municipio-dialog.component';
import {
  BusquedaAseguradoraDialogComponent,
  AseguradoraSeleccionada
} from '../shared/buscadores/buscador-aseguradora-dialog.component';
import { DatosBase } from './models/datos-base.model';
import { FormularioCrearTareaSinOcComponent } from './components/formulario-crear-tarea-sin-oc/formulario-crear-tarea-sin-oc.component';
import {
  CANAL,
  CANALES_SERVICIO_SIN_OC,
  ESTADOS_INVESTIGACION_SIN_OC,
  FORMAS_ENTREGA_LINEA_ATENCION,
  FORMAS_ENTREGA_ASESOR_CAMPO,
  RECURSO_CREAR_TAREA_SIN_OC,
  RECURSO_CUMPLIMIENTO_SIN_OC_MASIVO,
  RECURSO_GESTIONES_MASIVAS_SIN_OC,
  RECURSO_MODO_ENTREGA,
  TIPOS_LLAMADA_SIN_OC
} from 'src/app/common/constants-ejb';
import { CrearTareaSinOcFacade } from './services/crear-tarea-sin-oc.facade';
import { IdentityServiceImpl } from 'src/app/infrastructure/adapters/security/identity.service';
import { HasAccessDirective } from 'src/app/common/directives';

/**
 * Resultado exitoso retornado al componente padre.
 */
export interface TareaCreada {
  dsOrden: string;
  dsPlan: string;
  dsVersion: string;
  dsActividad: string;
}

/**
 * HU1132420 — Payload emitido al pulsar "Ingresar Cumplimiento" desde el
 * formulario Sin OC. Incluye la orden hidratada del backend más datos
 * capturados en este formulario que se deben mostrar en el formulario de
 * cumplimiento (Saldo por Ejecutar = cantidad capturada, Aseguradora).
 */
export interface IngresoCumplimientoPayload {
  hidratada: OrdenServicioDetalleHidratada;
  dsnombreAseguradora: string;
  dsnombreCliente: string;
  dsclasificacion: string;
  /**
   * Código de clasificación de la tarea homologada — es el "tipo de orden de
   * servicio" con el que el formulario de cumplimiento decide qué tipos de cita
   * puede ofrecer (Capacitación = '3' → solo Campo).
   */
  cdclasificacion: string;
}

/**
 * DiálogoCrearTareaSinOcComponent
 *
 * Formulario para crear una Tarea Sin Orden de Compra.
 * Replica el legacy GWT: CumplimientoSinOcViewImpl.java
 *
 * Reglas de negocio (verificadas en CumplimientoSinOcActivity.java):
 * - RN-01: Asesor debe tener la tarea homologada (FN_GET_SNTAREA_ASESOR)
 * - RN-02: fechaEntregaInicial y fechaEntregaFinal deben ser del mismo mes/año
 * - RN-03: cantidadTarea > 0
 * - RN-06: nmconsecTipoCita = 0 (inyectado en backend)
 * - RN-07: sntransporteAdic = 'S' solo para DOMICILIO o SKYPE_EMPRESARIAL
 *
 * @author Sura - Migración GWT a Angular
 */
@Component({
  selector: 'app-crear-tarea-sin-oc-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MessagesModule,
    ToastModule,
    LoadingOverlayComponent,
    BusquedaClienteDialogComponent,
    BusquedaCentroTrabajoDialogComponent,
    BusquedaTareaDialogComponent,
    BusquedaAsesorDialogComponent,
    BusquedaProductoDialogComponent,
    BusquedaMunicipioDialogComponent,
    BusquedaAseguradoraDialogComponent,
    HasAccessDirective,
    FormularioCrearTareaSinOcComponent
  ],
  providers: [MessageService, CrearTareaSinOcFacade],
  template: `
    <p-toast position="top-right" />
    <app-loading-overlay [blocked]="loading()" />

    <!-- ── Botonera superior (Legacy: <div id="botonera"> / panelAcciones) ── -->
    <div
      class="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-end sm:justify-start gap-2 sm:gap-3 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
    >
      @if (puedeVerResultadoCargaMasiva()) {
        <button
          pButton
          label="Resultado Carga Masiva"
          icon="pi pi-list"
          class="w-full sm:w-auto p-button-secondary p-button-outlined p-button-sm min-h-[44px]"
          (click)="abrirResultadoCargaMasiva()"
          [disabled]="loading()"
          pTooltip="Consultar resultado de carga masiva de cumplimientos Sin OC"
          tooltipPosition="bottom"
        ></button>
      }
      <!-- Legacy: saCumplimientoSinOCMasivo — CumplimientoSinOcViewImpl.java -->
      <button
        *hasAccess="recursoCumplimientoSinOcMasivo"
        pButton
        label="Cumplimientos Masivos"
        icon="pi pi-upload"
        class="w-full sm:w-auto p-button-secondary p-button-outlined p-button-sm min-h-[44px]"
        (click)="abrirCumplimientosMasivos()"
        [disabled]="loading()"
        pTooltip="Cargue masivo de cumplimientos sin orden de compra desde archivo Excel"
        tooltipPosition="bottom"
      ></button>
      <!-- Legacy: saMasivaGestiones — CumplimientoSinOcViewImpl.java -->
      <button
        *hasAccess="recursoGestionesMasivasSinOc"
        pButton
        label="Gestiones Masivas"
        icon="pi pi-upload"
        class="w-full sm:w-auto p-button-secondary p-button-outlined p-button-sm min-h-[44px]"
        (click)="abrirGestionesMasivas()"
        [disabled]="loading()"
        pTooltip="Cargue masivo de gestiones de llamada desde archivo Excel"
        tooltipPosition="bottom"
      ></button>
      <button
        pButton
        label="Ingresar Gestión"
        icon="pi pi-phone"
        class="w-full sm:w-auto p-button-info p-button-sm min-h-[44px]"
        (click)="abrirIngresarGestion()"
        [disabled]="!puedeIngresarGestion() || loading()"
        pTooltip="Registrar gestión de llamada para el cliente y oficina seleccionados"
        tooltipPosition="bottom"
      ></button>
    </div>

    <!-- Mensajes de error -->
    @if (mensajeError()) {
      <div
        #errorMsg
        class="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-300 text-sm whitespace-pre-line"
      >
        <i class="pi pi-exclamation-circle mr-2"></i>{{ mensajeError() }}
      </div>
    }

    <app-formulario-crear-tarea-sin-oc
      [form]="form"
      [loading]="loading()"
      [puedeCrearTarea]="puedeCrearTarea()"
      [oficinas]="oficinas()"
      [formasEntrega]="formasEntrega()"
      [tiposLlamada]="tiposLlamada"
      [canalesServicio]="canalesServicio"
      [estadosInvestigacion]="estadosInvestigacion"
      [aseguradoraLabel]="aseguradoraLabel()"
      [nombreBeneficiarioDisplay]="nombreBeneficiarioDisplay"
      [descripcionTarea]="descripcionTarea()"
      [asesorLabel]="asesorLabel()"
      [asesorBloqueado]="asesorBloqueado()"
      [esTelefonico]="esTelefonico()"
      [mostrarExpediente]="mostrarExpediente()"
      [nmconsecBeneficiarioLocal]="nmconsecBeneficiarioLocal"
      [municipioOrigen]="municipioOrigen()"
      [municipioEntrega]="municipioEntrega()"
      [municipioOrigenLabel]="municipioOrigenLabel()"
      [municipioEntregaLabel]="municipioEntregaLabel()"
      [fechaEntregaInicial]="fechaEntregaInicial"
      [fechaEntregaFinal]="fechaEntregaFinal"
      (oficinaChange)="onOficinaChange($event)"
      (formaEntregaChange)="onFormaEntregaChange()"
      (abrirBusquedaAseguradora)="dialogAseguradoraVisible = true"
      (limpiarAseguradora)="limpiarAseguradora()"
      (abrirBusquedaBeneficiario)="dialogBeneficiarioVisible = true"
      (limpiarBeneficiario)="limpiarBeneficiario()"
      (abrirBusquedaTarea)="dialogTareaVisible = true"
      (limpiarTarea)="limpiarTarea()"
      (abrirBusquedaCentroTrabajo)="dialogCentroTrabajoVisible = true"
      (limpiarCentroTrabajo)="limpiarCentroTrabajo()"
      (abrirBusquedaAsesor)="dialogAsesorVisible = true"
      (limpiarAsesor)="limpiarAsesor()"
      (abrirBusquedaProducto)="abrirBusquedaProducto()"
      (limpiarProducto)="limpiarProducto()"
      (abrirBusquedaMunicipioOrigen)="dialogMunicipioOrigenVisible = true"
      (limpiarMunicipioOrigen)="limpiarMunicipioOrigen()"
      (abrirBusquedaMunicipioEntrega)="dialogMunicipioEntregaVisible = true"
      (limpiarMunicipioEntrega)="limpiarMunicipioEntrega()"
      (fechaEntregaInicialChange)="fechaEntregaInicial = $event"
      (fechaEntregaFinalChange)="fechaEntregaFinal = $event"
      (limpiarFormulario)="limpiarFormulario()"
      (crearTarea)="crearTarea()"
      (ingresarCumplimiento)="ingresarCumplimiento()"
    />

    <!-- ═══ Sub-diálogos de búsqueda ═══ -->

    @if (dialogBeneficiarioVisible) {
      <!-- Legacy CumplimientoSinOcViewImpl: busca en M094_CUERPOLIZA por NPOLIZA -->
      <app-busqueda-cliente-dialog
        [origen]="BuscadorClienteOrigen.CUERPOLIZA_SIN_OC"
        (onSelect)="onBeneficiarioSeleccionado($event)"
        [titulo]="'Búsqueda de Beneficiario / Cliente'"
        [soloContratoYNombre]="true"
        (onClose)="dialogBeneficiarioVisible = false"
      />
    }

    @if (dialogCentroTrabajoVisible) {
      <!-- Legacy: T094_SUCURSALES WHERE NPOLIZA = ? para CUERPOLIZA, TPSC_PERSON_CENTRO_TRABA para BENEFICIARIO -->
      <app-busqueda-centro-trabajo-dialog
        [origen]="BuscadorCentroTrabajoOrigen.SUCURSAL_SIN_OC"
        [npoliza]="form.cdcontratoBeneficiario"
        [cdoficina]="cdoficinaBuscadorSucursal()"
        [titulo]="'Búsqueda de Centro de Trabajo'"
        (onSelect)="onCentroTrabajoSeleccionado($event)"
        (onClose)="dialogCentroTrabajoVisible = false"
      />
    }

    @if (dialogTareaVisible) {
      <app-busqueda-tarea-dialog
        [titulo]="'Búsqueda de Tarea'"
        [filtrarFebaja]="true"
        (onSelect)="onTareaSeleccionada($event)"
        (onClose)="dialogTareaVisible = false"
      />
    }

    @if (dialogAsesorVisible) {
      <app-asesor-search
        (onSelect)="onAsesorSeleccionado($event)"
        (onClose)="dialogAsesorVisible = false"
      />
    }

    @if (dialogMunicipioOrigenVisible) {
      <app-busqueda-municipio-dialog
        [titulo]="'Búsqueda de Municipio Origen'"
        (onSelect)="onMunicipioOrigenSeleccionado($event)"
        (onClose)="dialogMunicipioOrigenVisible = false"
      />
    }

    @if (dialogMunicipioEntregaVisible) {
      <app-busqueda-municipio-dialog
        [titulo]="'Búsqueda de Municipio Entrega'"
        (onSelect)="onMunicipioEntregaSeleccionado($event)"
        (onClose)="dialogMunicipioEntregaVisible = false"
      />
    }

    @if (dialogProductoVisible) {
      <app-busqueda-producto-dialog
        [cdtarea]="form.cdservicio"
        (onSelect)="onProductoSeleccionado($event)"
        (onClose)="dialogProductoVisible = false"
      />
    }

    @if (dialogAseguradoraVisible) {
      <app-busqueda-aseguradora-dialog
        (aseguradoraSelected)="onAseguradoraSeleccionada($event)"
        (dialogClosed)="dialogAseguradoraVisible = false"
      />
    }
  `
})
export class CrearTareaSinOcDialogComponent implements OnInit {
  @ViewChild('errorMsg') private errorMsgRef?: ElementRef<HTMLElement>;

  // Expuesto al template para los bindings [origen]
  readonly BuscadorClienteOrigen = BuscadorClienteOrigen;
  readonly BuscadorCentroTrabajoOrigen = BuscadorCentroTrabajoOrigen;

  // ── Ports ──────────────────────────────────────────────────────────────────
  private readonly sinOcRepository = inject(SinOcRepository);
  private readonly messageService = inject(MessageService);
  private readonly crearTareaFacade = inject(CrearTareaSinOcFacade);
  private readonly identityService = inject(IdentityServiceImpl);

  // ── Outputs ────────────────────────────────────────────────────────────────
  onCreada = output<TareaCreada>();
  // Output para exponer el modelo del formulario principal
  onDatosBase = output<DatosBase>();
  /** HU1132420 — emite la orden hidratada lista para abrir el FormularioCumplimiento NO_PROGRAMADA */
  onAbrirIngresoCumplimiento = output<IngresoCumplimientoPayload>();
  readonly recursoCumplimientoSinOcMasivo = RECURSO_CUMPLIMIENTO_SIN_OC_MASIVO;
  readonly recursoGestionesMasivasSinOc = RECURSO_GESTIONES_MASIVAS_SIN_OC;
  /**
   * Legacy: siCrearTarea (seguro="true") — oculto hasta que SEUS confirme el acceso;
   * ante error o sin respuesta permanece oculto (política cerrada del legacy).
   */
  readonly puedeCrearTarea = signal(false);
  readonly puedeVerCumplimientoSinOcMasivo = signal(false);
  readonly puedeVerGestionesMasivasSinOc = signal(false);
  readonly puedeVerResultadoCargaMasiva = computed(
    () => this.puedeVerCumplimientoSinOcMasivo() || this.puedeVerGestionesMasivasSinOc()
  );

  // ── Catálogos estáticos ────────────────────────────────────────────────────
  /**
   * Legacy: UtilidadesBuscadores.llenarListBoxFormaEntrega() — la lista se
   * decide en runtime según `Utilidades.hasAccess("rsSelModoEntrega", ...)`.
   * Con acceso (línea de atención) → FORMAS_ENTREGA_LINEA_ATENCION.
   * Sin acceso (asesor de campo)  → FORMAS_ENTREGA_ASESOR_CAMPO.
   * Política ante error del servicio de identidad: aplicar la rama denyCb
   * (asesor de campo) — es la rama restrictiva del legacy.
   */
  readonly formasEntrega = signal<Array<{ label: string; value: string }>>(
    FORMAS_ENTREGA_ASESOR_CAMPO
  );
  /**
   * Modo línea de atención (acceso a rsSelModoEntrega → FORMAS_ENTREGA_LINEA_ATENCION).
   * Legacy: CumplimientoSinOcViewImpl.isCumpLineaAt() — la lista de formas de entrega
   * no contiene DOMICILIO ni SKYPE_EMPRESARIAL.
   */
  readonly esLineaAtencion = signal(false);
  readonly tiposLlamada = TIPOS_LLAMADA_SIN_OC;
  readonly canalesServicio = CANALES_SERVICIO_SIN_OC;
  readonly estadosInvestigacion = ESTADOS_INVESTIGACION_SIN_OC;

  // ── Estado ─────────────────────────────────────────────────────────────────
  loading = signal(false);
  mensajeError = signal<string | null>(null);
  oficinas = signal<Oficina[]>([]);

  // Municipios
  municipioOrigen = signal<Municipio | null>(null);
  municipioEntrega = signal<Municipio | null>(null);
  municipioOrigenLabel = () =>
    this.municipioOrigen()
      ? `${this.municipioOrigen()!.dsmunicipio} (${this.municipioOrigen()!.cdmunicipio})`
      : '';
  municipioEntregaLabel = () =>
    this.municipioEntrega()
      ? `${this.municipioEntrega()!.dsmunicipio} (${this.municipioEntrega()!.cdmunicipio})`
      : '';

  // Asesor
  asesorSeleccionado = signal<Persona | null>(null);
  asesorLabel = () =>
    this.asesorSeleccionado()
      ? getNombreCompleto(this.asesorSeleccionado()!)
      : '';
  /**
   * Legacy: CumplimientoSinOcViewImpl.initBuscadorAsesor()
   * Si es true, el usuario es asesor (no pymes) → campo Asesor bloqueado.
   */
  asesorBloqueado = signal(false);

  // Fechas (como Date para el calendario)
  fechaEntregaInicial: Date | null = null;
  fechaEntregaFinal: Date | null = null;

  esTelefonico = signal(false);

  /**
   * Legacy: setVisibleExpediente(boolean visible)
   * Se activa cuando el cdservicio de la tarea seleccionada
   * está en la lista TAREAS_VALIDAS_EXPEDIENTE de TPSC_PARAMETROS_GENERALES.
   * Ambos campos (Expediente y Estado de Investigación) son obligatorios si visible.
   */
  mostrarExpediente = signal(false);
  /** Lista cargada desde TPSC_PARAMETROS_GENERALES (CDPARAMETRO='TAREAS_EXPEDIENTE') */
  private tareasValidasExpediente: string[] = [];

  // Diálogos visibles
  dialogBeneficiarioVisible = false;
  dialogCentroTrabajoVisible = false;
  dialogTareaVisible = false;
  dialogAsesorVisible = false;
  dialogMunicipioOrigenVisible = false;
  dialogMunicipioEntregaVisible = false;
  dialogProductoVisible = false;
  dialogAseguradoraVisible = false;

  // ── Estado local (no en el request) ────────────────────────────────────────
  /** nmconsecutivo del beneficiario seleccionado — usado para filtrar CT dialog */
  nmconsecBeneficiarioLocal = 0;
  /** Nombre del beneficiario para mostrar en el input (no va en el request) */
  nombreBeneficiarioDisplay = '';
  /** Label aseguradora seleccionada para mostrar en el input de búsqueda */
  aseguradoraLabel = signal<string>('');
  /** Label de la tarea seleccionada (cdservicio - dsservicio - dsclasificacion) tal cual la fila del listado */
  descripcionTarea = signal<string>('');
  /**
   * CDCLASIFICACION de la tarea seleccionada (tipo de orden de servicio). No viaja
   * en el request de creación, pero sí al formulario de cumplimiento para filtrar
   * los tipos de cita disponibles (Capacitación → solo Campo).
   */
  cdclasificacionTarea = '';

  // ── Form model ─────────────────────────────────────────────────────────────
  // Legacy: CumplimientoSinOcViewImpl.initListBox() + llenarListBoxCanalServicio()
  // deja seleccionado por defecto "Canal consultor".
  form: Partial<CrearTareaRequest> = { dscanalServicio: CANAL.CONSULTOR };

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.cargarFormasEntrega();
    this.cargarPermisosAccionesMasivas();
    this.cargarOficinas();
    this.cargarTareasValidasExpediente();
    this.cargarAseguradoraDefault();
    this.initBuscadorAsesor();
  }

  /**
   * Legacy: UtilidadesBuscadores.llenarListBoxFormaEntrega() decide la lista
   * a partir de {@code hasAccess("rsSelModoEntrega")}. Aquí replicamos ese
   * mismo comportamiento consultando el mismo recurso SEUS.
   */
  private cargarFormasEntrega(): void {
    this.identityService.isAuthorized(RECURSO_MODO_ENTREGA).subscribe({
      next: (autorizado) => {
        this.formasEntrega.set(
          autorizado ? FORMAS_ENTREGA_LINEA_ATENCION : FORMAS_ENTREGA_ASESOR_CAMPO
        );
        this.esLineaAtencion.set(autorizado);
      },
      error: () => {
        this.formasEntrega.set(FORMAS_ENTREGA_ASESOR_CAMPO);
        this.esLineaAtencion.set(false);
      }
    });
  }

  private cargarPermisosAccionesMasivas(): void {
    this.identityService.isAuthorized(RECURSO_CREAR_TAREA_SIN_OC).subscribe({
      next: (autorizado) => this.puedeCrearTarea.set(autorizado === true),
      error: () => this.puedeCrearTarea.set(false)
    });

    this.identityService.isAuthorized(RECURSO_CUMPLIMIENTO_SIN_OC_MASIVO).subscribe({
      next: (autorizado) => this.puedeVerCumplimientoSinOcMasivo.set(autorizado),
      error: () => this.puedeVerCumplimientoSinOcMasivo.set(false)
    });

    this.identityService.isAuthorized(RECURSO_GESTIONES_MASIVAS_SIN_OC).subscribe({
      next: (autorizado) => this.puedeVerGestionesMasivasSinOc.set(autorizado),
      error: () => this.puedeVerGestionesMasivasSinOc.set(false)
    });
  }

  private async cargarOficinas(): Promise<void> {
    try {
      const todas = await this.crearTareaFacade.cargarOficinasYAplicarDefault(
        this.form
      );
      this.oficinas.set(todas);
    } catch {
      // Si falla, continuamos sin oficinas precargadas
    }
  }

  /**
   * Legacy: CumplimientoSinOcViewImpl.finaliza() → al interactuar con Forma de Entrega
   * preselecciona la oficina del empleado en sesión
   * (ApplicationCommonData.getSessionUser().getNmconsec_oficina()).
   * Aquí se aplica al inicializar el diálogo, una vez cargada la lista de oficinas.
   */
  private async aplicarOficinaDefaultEmpleado(
    disponibles: Oficina[]
  ): Promise<void> {
    await this.crearTareaFacade.aplicarOficinaDefaultEmpleado(
      this.form,
      disponibles
    );
  }

  /**
   * Legacy: GeneralParameters.getParameterValue(GeneralParameters.TAREAS_VALIDAS_EXPEDIENTE)
   * Carga la lista de cdservicio que requieren campos Expediente + Estado de Investigación.
   * Fuente: TPSC_PARAMETROS_GENERALES (CDPARAMETRO = 'TAREAS_EXPEDIENTE').
   */
  private async cargarTareasValidasExpediente(): Promise<void> {
    this.tareasValidasExpediente =
      await this.crearTareaFacade.cargarTareasValidasExpediente();
  }

  /**
   * Legacy: identificationClienteOrden preloaded with CLIENTE_DEFAULT_CIA.
    * Usa contrato dedicado backend para resolver CLIENTE_DEFAULT_CIA
    * y cargar la aseguradora por defecto.
   * Mismo patrón que FilterOrdenesNoProgViewImpl / busqueda-ordenes.component.ts.
   */
  private async cargarAseguradoraDefault(): Promise<void> {
    const label = await this.crearTareaFacade.cargarAseguradoraDefault(
      this.form
    );
    this.aseguradoraLabel.set(label);
  }

  /**
   * Legacy: CumplimientoSinOcViewImpl.initBuscadorAsesor()
   * Si el usuario es tipo asesor (CDTIPO_EMP_ASES = 'A'):
   *   - Auto-llena el campo Asesor con el usuario en sesión
   *   - Si snpymes !== 'S' → oculta el buscador (campo bloqueado)
   *   - Si snpymes === 'S' → deja el buscador visible (puede cambiar)
   * Si NO es asesor → campo vacío y buscador visible.
   */
  private async initBuscadorAsesor(): Promise<void> {
    const resultado = await this.crearTareaFacade.initBuscadorAsesor(this.form);
    this.asesorSeleccionado.set(resultado.asesorSeleccionado);
    this.asesorBloqueado.set(resultado.asesorBloqueado);
  }

  // ── Selección aseguradora ──────────────────────────────────────────────────
  onAseguradoraSeleccionada(a: AseguradoraSeleccionada): void {
    this.form.nmconsecCliente = a.nmconsecutivo;
    this.aseguradoraLabel.set(a.nombreComercial);
    this.dialogAseguradoraVisible = false;
  }

  limpiarAseguradora(): void {
    this.form.nmconsecCliente = undefined;
    this.aseguradoraLabel.set('');
  }

  // ── Evento de oficina ──────────────────────────────────────────────────────
  onOficinaChange(event: { value: number }): void {
    const oficina = this.oficinas().find(
      (o) => o.nmconsecutivo === event.value
    );
    if (oficina) {
      this.form.cdoficina = oficina.cdoficina;
      this.form.nmconsecOficina = oficina.nmconsecutivo;
    }
  }

  // ── Evento forma de entrega ────────────────────────────────────────────────
  onFormaEntregaChange(): void {
    this.esTelefonico.set(
      this.crearTareaFacade.debeMostrarTipoLlamada(this.form.formaEntrega)
    );
    if (!this.esTelefonico()) {
      this.form.tipoLlamada = undefined;
    }
  }

  // ── Selección beneficiario ─────────────────────────────────────────────────
  onBeneficiarioSeleccionado(cliente: ClienteSeleccionado): void {
    // nmconsecCliente es la ASEGURADORA — no se modifica aquí
    this.nmconsecBeneficiarioLocal = cliente.nmconsecutivo;
    this.nombreBeneficiarioDisplay = cliente.nombreComercial; // solo para UI
    // Legacy: persona.setDni(CP.DNI) y persona.setDsnombre_comercial(CP.DSCOMERCIAL). Se envía tal cual:
    // SP_CREAR_BENEF_CT / SP_PSC_CREAR_BENEFICI_PERSONA crean la persona si no existe en TPSC_PERSONAS.
    this.form.dniBeneficiario = cliente.dniCompleto?.trim() ?? '';
    this.form.dsnombreBeneficiario = cliente.nombreComercial?.trim() ?? '';
    this.form.cdpersonaBeneficiario = cliente.cdpersona || undefined; // informativo (0 = sin persona)
    this.form.cdcontratoBeneficiario = cliente.contrato ?? '';
    // Limpiar CT al cambiar beneficiario
    this.limpiarCentroTrabajo();
    this.dialogBeneficiarioVisible = false;
    if (this.esLineaAtencion()) {
      void this.aplicarPolizaLineaAtencion(cliente);
    }
  }

  /**
   * Legacy: CumplimientoSinOcViewImpl.seleccionaronItem() líneas 804-816 (modo línea de
   * atención): preselecciona la oficina de la póliza (CDDELEGACION) y toma como centro de
   * trabajo la primera sucursal de esa oficina (setObtenerPrimerRegistro).
   */
  private async aplicarPolizaLineaAtencion(cliente: ClienteSeleccionado): Promise<void> {
    this.crearTareaFacade.aplicarOficinaPorDelegacion(
      this.form,
      this.oficinas(),
      cliente.cddelegacion
    );
    const npoliza = this.form.cdcontratoBeneficiario;
    const primera = await this.crearTareaFacade.obtenerPrimerCentroTrabajoLineaAtencion(
      npoliza,
      this.form.cdoficina
    );
    // Ignorar la respuesta si mientras tanto cambió la póliza o ya se eligió un centro.
    if (primera && npoliza === this.form.cdcontratoBeneficiario && !this.form.cdcentroTrabajo) {
      this.form.cdcentroTrabajo = primera.cdcentroTrabajo;
      this.form.dscentroTrabajo = primera.dscentroTrabajo;
    }
  }

  /** CDOFICINA que filtra el buscador de sucursales: solo en modo línea de atención. */
  cdoficinaBuscadorSucursal(): string | undefined {
    return this.esLineaAtencion() ? this.form.cdoficina : undefined;
  }

  limpiarBeneficiario(): void {
    this.nmconsecBeneficiarioLocal = 0;
    this.nombreBeneficiarioDisplay = '';
    this.form.dniBeneficiario = undefined;
    this.form.dsnombreBeneficiario = undefined;
    this.form.cdpersonaBeneficiario = undefined;
    this.form.cdcontratoBeneficiario = undefined;
    this.limpiarCentroTrabajo();
  }

  // ── Selección centro de trabajo ────────────────────────────────────────────
  onCentroTrabajoSeleccionado(ct: CentroTrabajoSeleccionado): void {
    this.form.cdcentroTrabajo = ct.cdcentroTrabajo;
    this.form.dscentroTrabajo = ct.dscentroTrabajo;
    this.dialogCentroTrabajoVisible = false;
  }

  limpiarCentroTrabajo(): void {
    this.form.cdcentroTrabajo = undefined;
    this.form.dscentroTrabajo = undefined;
  }

  // ── Selección tarea ────────────────────────────────────────────────────────
  onTareaSeleccionada(tarea: TareaSeleccionada): void {
    this.form.nmconsecHomologacion = tarea.nmconsecutivo;
    this.form.cdservicio = tarea.cdservicio;
    this.form.dsclasificacion = tarea.dsclasificacion ?? '';
    this.cdclasificacionTarea = tarea.cdclasificacion ?? '';
    this.form.nmconsecServicioCliente = 0; // se asigna en backend
    // Mostrar en el input el mismo texto que el usuario vio en el listado del buscador
    this.descripcionTarea.set(tarea.descripcionCompleta);
    // Legacy: buscadorProductos.limpiar() en setHomologTareaDto
    this.limpiarProducto();
    // Legacy: setVisibleExpediente(Arrays.asList(...).contains(cdservicio))
    // Muestra Expediente y Estado de Investigación si la tarea lo requiere
    const requiereExpediente = this.crearTareaFacade.requiereExpediente(
      tarea.cdservicio,
      this.tareasValidasExpediente
    );
    this.mostrarExpediente.set(requiereExpediente);
    if (!requiereExpediente) {
      // Limpiar valores si la tarea no requiere expediente
      this.form.nmexpediente = undefined;
      this.form.dsestadoInvestigacion = undefined;
    }
    this.dialogTareaVisible = false;
  }

  limpiarTarea(): void {
    this.form.nmconsecHomologacion = undefined;
    this.form.cdservicio = undefined;
    this.form.dsclasificacion = undefined;
    this.cdclasificacionTarea = '';
    this.form.nmconsecServicioCliente = undefined;
    this.descripcionTarea.set('');
    // Legacy: reset() llama setVisibleExpediente(false)
    this.mostrarExpediente.set(false);
    this.form.nmexpediente = undefined;
    this.form.dsestadoInvestigacion = undefined;
    this.limpiarProducto();
  }

  // ── Selección producto ───────────────────────────────────────────
  /**
   * Legacy: buscadorProductos.setOnSearchValidator
   * Valida que exista una tarea seleccionada antes de abrir el buscador.
   * Si no hay tarea muestra alerta (cons.alertSelecTarea()).
   */
  abrirBusquedaProducto(): void {
    if (!this.form.cdservicio) {
      // Legacy: Utilidades.showDialogMessage(cons.alertSelecTarea())
      this.messageService.add({
        severity: 'warn',
        summary: 'Tarea requerida',
        detail: 'Debe seleccionar una Tarea antes de buscar el Producto.',
        life: 4000
      });
      return;
    }
    this.dialogProductoVisible = true;
  }

  onProductoSeleccionado(producto: ProductoSeleccionado): void {
    this.form.cdproducto = producto.cdproducto;
    this.form.dsproducto = producto.dsproducto;
    this.dialogProductoVisible = false;
  }

  limpiarProducto(): void {
    this.form.cdproducto = undefined;
    this.form.dsproducto = undefined;
  }

  // ── Selección asesor ───────────────────────────────────────────────────────
  onAsesorSeleccionado(asesor: AsesorSeleccionado): void {
    const persona: Persona = {
      cdpersona: asesor.cdpersona,
      cdtipo_identificacion: asesor.cdtipoIdentificacion,
      nmidentificacion: asesor.nmidentificacion,
      dsnombre1: asesor.dsnombre1,
      dsnombre2: asesor.dsnombre2,
      dsapellido1: asesor.dsapellido1,
      dsapellido2: asesor.dsapellido2,
      dsnombre_comercial: asesor.dsnombreComercial,
      snactivo: asesor.snactivo
    } as Persona;
    this.form.cdpersonaAsesor = asesor.cdpersona;
    this.asesorSeleccionado.set(persona);
    this.dialogAsesorVisible = false;
  }

  limpiarAsesor(): void {
    this.form.cdpersonaAsesor = undefined;
    this.asesorSeleccionado.set(null);
  }

  // ── Municipios ─────────────────────────────────────────────────────────────
  onMunicipioOrigenSeleccionado(m: MunicipioSeleccionado): void {
    this.municipioOrigen.set({
      cdmunicipio: m.cdmunicipio,
      dsmunicipio: m.dsmunicipio,
      cdsura: m.cdsura
    });
    this.form.cdmunicipioOrigen = m.cdmunicipio;
    this.dialogMunicipioOrigenVisible = false;
  }

  onMunicipioEntregaSeleccionado(m: MunicipioSeleccionado): void {
    this.municipioEntrega.set({
      cdmunicipio: m.cdmunicipio,
      dsmunicipio: m.dsmunicipio,
      cdsura: m.cdsura
    });
    this.form.cdmunicipioEntrega = m.cdmunicipio;
    this.dialogMunicipioEntregaVisible = false;
  }

  limpiarMunicipioOrigen(): void {
    this.municipioOrigen.set(null);
    this.form.cdmunicipioOrigen = undefined;
  }

  limpiarMunicipioEntrega(): void {
    this.municipioEntrega.set(null);
    this.form.cdmunicipioEntrega = undefined;
  }

  /**
   * Legacy: llenarListBoxCanalServicio() selecciona "Canal consultor" por defecto.
   * Asegura que el valor exista en el modelo aunque el usuario no interactúe con el combo.
   */
  private asegurarCanalServicioDefault(): void {
    this.crearTareaFacade.ensureCanalServicioDefault(this.form);
  }

  // ── Validaciones de UI ─────────────────────────────────────────────────────
  /**
   * Legacy: validarCamposRequeridos(true) — para botón "Crear Tarea"
   * Valida campos comunes + fechas + cantidad + expediente (si aplica).
   */
  private validar(): string | null {
    return this.crearTareaFacade.validarCrearTarea({
      form: this.form,
      esTelefonico: this.esTelefonico(),
      mostrarExpediente: this.mostrarExpediente(),
      fechaEntregaInicial: this.fechaEntregaInicial,
      fechaEntregaFinal: this.fechaEntregaFinal
    });
  }

  // ── Crear tarea ────────────────────────────────────────────────────────────
  async crearTarea(): Promise<void> {
    if (!this.puedeCrearTarea()) {
      return;
    }
    this.mensajeError.set(null);
    this.asegurarCanalServicioDefault();

    const error = this.validar();
    if (error) {
      this.mensajeError.set(error);
      setTimeout(() =>
        this.errorMsgRef?.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      );
      return;
    }

    this.loading.set(true);
    try {
      const request: CrearTareaRequest =
        this.crearTareaFacade.construirRequestCrearTarea(
          this.form,
          this.fechaEntregaInicial!,
          this.fechaEntregaFinal!
        );

      const resultado: CrearTareaResponse =
        await this.sinOcRepository.crearTarea(request);

      this.messageService.add({
        severity: 'success',
        summary: 'Tarea Creada',
        detail: `Orden ${resultado.dsOrden} creada exitosamente.`
      });

      this.onCreada.emit({
        dsOrden: resultado.dsOrden,
        dsPlan: resultado.dsPlan,
        dsVersion: resultado.dsVersion,
        dsActividad: resultado.dsActividad
      });
    } catch (err: unknown) {
      const msg = this.extraerMensajeError(err);
      this.mensajeError.set(msg);
      setTimeout(() =>
        this.errorMsgRef?.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      );
    } finally {
      this.loading.set(false);
    }
  }

  private extraerMensajeError(err: unknown): string {
    return this.crearTareaFacade.extraerMensajeError(err);
  }

  // ── Limpiar formulario ─────────────────────────────────────────────────────
  private resetForm(): void {
    const resetState = this.crearTareaFacade.resetFormularioState();
    this.form = resetState.form;
    this.fechaEntregaInicial = resetState.fechaEntregaInicial;
    this.fechaEntregaFinal = resetState.fechaEntregaFinal;
    this.municipioOrigen.set(null);
    this.municipioEntrega.set(null);
    this.asesorSeleccionado.set(null);
    this.esTelefonico.set(resetState.esTelefonico);
    // Legacy: reset(null) → setVisibleExpediente(false)
    this.mostrarExpediente.set(resetState.mostrarExpediente);
    this.mensajeError.set(resetState.mensajeError);
    // Limpiar beneficiario local
    this.nmconsecBeneficiarioLocal = resetState.nmconsecBeneficiarioLocal;
    this.nombreBeneficiarioDisplay = resetState.nombreBeneficiarioDisplay;
    // Limpiar label de tarea (input "Tarea / Servicio")
    this.descripcionTarea.set('');
    this.cdclasificacionTarea = '';
    // Recargar aseguradora por defecto
    this.cargarAseguradoraDefault();
    // Reaplicar oficina del empleado en sesión (como al abrir el formulario por primera vez)
    void this.aplicarOficinaDefaultEmpleado(this.oficinas());
    // Legacy: initBuscadorAsesor() — reaplicar auto-selección si es asesor
    void this.initBuscadorAsesor();
  }

  // ── Limpiar formulario (público, llamado desde botón) ───────────────────────
  limpiarFormulario(): void {
    this.resetForm();
  }

  // ── Ingresar Cumplimiento ──────────────────────────────────────────────────
  /**
   * HU1132420 — Botón "Ingresar Cumplimiento" del legacy GWT
   * (CumplimientoSinOcViewImpl.onClickIngCumplimiento → validarCamposRequeridos(false)
   * → CumplimientoSinOcActivity.ingresarCumplimiento(dto)).
   *
   * Valida los campos comunes del formulario (sin fechas ni cantidad), llama al
   * endpoint /preparar-ingreso-cumplimiento y emite la orden hidratada al
   * contenedor para abrir el FormularioCumplimientoComponent en NO_PROGRAMADA.
   */
  async ingresarCumplimiento(): Promise<void> {
    this.mensajeError.set(null);
    this.asegurarCanalServicioDefault();

    const error = this.validarSinFechasCantidad();
    if (error) {
      this.mensajeError.set(error);
      setTimeout(() =>
        this.errorMsgRef?.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      );
      return;
    }

    this.loading.set(true);
    try {
      const request: PrepararIngresoCumplimientoRequest =
        this.crearTareaFacade.construirRequestPrepararIngresoCumplimiento(
          this.form
        );

      const hidratada: OrdenServicioDetalleHidratada =
        await this.sinOcRepository.prepararIngresoCumplimiento(request);

      this.messageService.add({
        severity: 'success',
        summary: 'Listo',
        detail: 'Datos validados, abriendo formulario de cumplimiento.',
        life: 2500
      });

      this.onAbrirIngresoCumplimiento.emit({
        hidratada,
        dsnombreAseguradora: this.aseguradoraLabel(),
        dsnombreCliente: this.nombreBeneficiarioDisplay || '',
        dsclasificacion: this.form.dsclasificacion || '',
        cdclasificacion: this.cdclasificacionTarea
      });
    } catch (err: unknown) {
      const msg = this.extraerMensajeError(err);
      this.mensajeError.set(msg);
      setTimeout(() =>
        this.errorMsgRef?.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      );
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Validación equivalente a validarCamposRequeridos(false) del legacy:
   * mismos campos comunes que validar() pero SIN fechas ni cantidad.
   */
  private validarSinFechasCantidad(): string | null {
    return this.crearTareaFacade.validarIngresoCumplimiento({
      form: this.form,
      esTelefonico: this.esTelefonico(),
      mostrarExpediente: this.mostrarExpediente()
    });
  }

  // Legacy: saIngresarGestion — precondición de apertura del popup (HU1132422)
  // Condición: beneficiarioDto.getCdcontrato() no vacío Y lbOficinaOrden != null
  puedeIngresarGestion(): boolean {
    return this.crearTareaFacade.puedeIngresarGestion(this.form);
  }

  // Handler para mostrar el modal de gestión
  abrirIngresarGestion() {
    this.onDatosBase.emit(this.form as DatosBase);
    this.onAbrirGestion.emit();
  }

  // Handler para mostrar el modal de resultado carga masiva
  abrirResultadoCargaMasiva() {
    this.onAbrirResultadoCargaMasiva.emit();
  }

  // Legacy: saMasivaGestiones — CumplimientoSinOcViewImpl.java
  // Handler para abrir el modal de gestiones masivas
  abrirGestionesMasivas() {
    this.onAbrirGestionesMasivas.emit();
  }

  // Legacy: saCumplimientoSinOCMasivo — CumplimientoSinOcViewImpl.java
  // Handler para abrir el modal de cumplimientos masivos
  abrirCumplimientosMasivos() {
    this.onAbrirCumplimientosMasivos.emit();
  }

  // Output para notificar apertura del modal
  onAbrirGestion = output<void>();
  onAbrirResultadoCargaMasiva = output<void>();
  onAbrirGestionesMasivas = output<void>();
  onAbrirCumplimientosMasivos = output<void>();
}
